import React, { useEffect, useCallback, useRef } from 'react';
import { PaymentReturnPage } from '../components/payment-return-page.component';
import { useShopStore } from '../../../redux/shop';
import { useCustomerStore } from '../../../redux/customers';
import { useToastStore } from '../../../redux/toast';
import { useProfilePackageStore } from '../../../redux/profile-packages';
import { useOrderStore } from '../../../redux/orders';
import { useFinancialTransactionStore } from '../../../redux/financial-transactions';
import { clearCache } from '../../../services/api/responseCache';
import { useHistory, useLocation } from 'react-router-dom';

export const PaymentReturnPageContainer: React.FC = () => {
    const location = useLocation();
    const history = useHistory();
    const searchParams = new URLSearchParams(location.search.replace(/&amp;/g, '&'));
    const hashParams = new URLSearchParams(location.hash.split('?')[1]?.replace(/&amp;/g, '&') || '');

    const getParam = (key: string) => searchParams.get(key) || hashParams.get(key);
    const proformaId = getParam('proforma') || getParam('proformaId') || getParam('proforma_id') || getParam('proformaid') || getParam('proformaUuid') || getParam('proforma_uuid');

    const shopStore = useShopStore();
    const { proforma, proformaStatus, isFetching, checkProformaStatus, clearProformaStatus, errorMessage, clearErrors } = shopStore;

    const customerStore = useCustomerStore();
    const { activeCustomer } = customerStore;
    const toastStore = useToastStore();

    const { setSelectedId } = useProfilePackageStore();

    const orderStore = useOrderStore();
    const financialTransactionStore = useFinancialTransactionStore();

    useEffect(() => {
        // Clear proforma status on mount or when proformaId changes to ensure fresh check
        clearProformaStatus();
    }, [proformaId, clearProformaStatus]);

    useEffect(() => {
        if (activeCustomer && proformaId && !proformaStatus && !isFetching) {
            checkProformaStatus({ customerId: activeCustomer.id, proformaId })
                .catch(() => {
                    // Rejection handled by the shop store
                });
        }
    }, [activeCustomer, proformaId, proformaStatus, isFetching, checkProformaStatus]);

    useEffect(() => {
        if (errorMessage) {
            toastStore.showError({ message: errorMessage }).then(() => {
                clearErrors();
            });
        }
    }, [errorMessage, toastStore, clearErrors]);

    const handleCheckStatus = useCallback(() => {
        if (activeCustomer && proformaId) {
            checkProformaStatus({ customerId: activeCustomer.id, proformaId })
                .catch(() => {
                    // Rejection handled by the shop store
                });
        }
    }, [activeCustomer, proformaId, checkProformaStatus]);

    const handleBookNow = useCallback(async () => {
        const bookableId = proformaStatus?.bookableProfilePackageId;
        if (bookableId) {
            await setSelectedId({ id: bookableId });
            // A single replace only: a replace+push pair issued back-to-back gets
            // batched by React 18 into one render, so Ionic's router never processes
            // the intermediate route and its view stack desyncs from the URL —
            // resurfacing this page later as a stale "Invalid Proforma ID" view.
            history.replace(`/slots/${bookableId}`);
        }
    }, [proformaStatus?.bookableProfilePackageId, setSelectedId, history]);

    const mountedAtRef = useRef(Date.now());
    const refetchedRef = useRef(false);

    useEffect(() => {
        // On a confirmed-paid return, the account balance / orders the user just paid
        // against are stale: nothing refetched them and `financial-transactions` is
        // cached for 30s, so Account (and its pull-to-refresh) would serve old bytes.
        // Drop the read cache and refetch orders + financial-transactions once, and
        // only off a status checked during THIS mount (never a rehydrated one).
        const isPaid = proformaStatus?.isPaid;
        const checkedAt = proformaStatus?.checkedAt;
        const checkedThisMount = !!checkedAt && new Date(checkedAt).getTime() >= mountedAtRef.current;
        if (isPaid && checkedThisMount && activeCustomer && !refetchedRef.current) {
            refetchedRef.current = true;
            clearCache();
            orderStore.fetchOrders({ customerId: activeCustomer.id }).catch(() => {
                // Refetch failures surface through their own slice; nothing to do here.
            });
            financialTransactionStore.getAll({ customerId: activeCustomer.id }).catch(() => {
                // Refetch failures surface through their own slice; nothing to do here.
            });
        }
    }, [proformaStatus?.isPaid, proformaStatus?.checkedAt, activeCustomer, orderStore, financialTransactionStore]);

    useEffect(() => {
        // Only auto-book if canBook is true and we have an explicit ID from the API,
        // and only off a status checked during this mount — a status that predates it
        // (e.g. rehydrated from a previous order) must never trigger a booking.
        const bookableId = proformaStatus?.bookableProfilePackageId;
        const checkedAt = proformaStatus?.checkedAt;
        const checkedThisMount = !!checkedAt && new Date(checkedAt).getTime() >= mountedAtRef.current;
        if (proformaStatus?.canBook && bookableId && checkedThisMount) {
            handleBookNow();
        }
    }, [proformaStatus?.canBook, proformaStatus?.bookableProfilePackageId, proformaStatus?.checkedAt, handleBookNow]);


    if (!proformaId) {
        return (
            <PaymentReturnPage
                customer={activeCustomer}
                proformaId="N/A"
                proforma={null}
                isFetching={isFetching}
                onCheckStatus={handleCheckStatus}
                errorMessage="Invalid Proforma ID."
            />
        );
    }

    return (
        <PaymentReturnPage
            customer={activeCustomer}
            proformaId={proformaId}
            proforma={proforma}
            proformaStatus={proformaStatus}
            isFetching={isFetching}
            onCheckStatus={handleCheckStatus}
            onBookNow={(proformaStatus?.canBook && proformaStatus?.bookableProfilePackageId) ? handleBookNow : undefined}
            errorMessage={errorMessage}
        />
    );
};
